/**
 * @copyright Copyright 2023 Epic Systems Corporation
 * @file Shared state for Picture-in-Picture feature
 * @author Arun Vasireddy
 * @module Epic.VideoApp.State.PictureInPicture
 */

import { buildSharedState } from "@epic/react-redux-booster";
import store from "~/app/store";
import { VideoType } from "~/web-core/types";

/// TYPES ///

export interface IPictureInPictureState {
	isPiPModeEnabled: boolean;
	isHeaderButtonPressed: boolean;
	videoID: string;
	videoType: VideoType | "";
}

/// INIT ///

function getInitialState(): IPictureInPictureState {
	return {
		isPiPModeEnabled: false,
		isHeaderButtonPressed: false,
		videoID: "",
		videoType: "",
	};
}

/// REDUCERS ///

function togglePiPMode(
	state: IPictureInPictureState,
	value: { videoID?: string; videoType?: VideoType; fromHeader: boolean },
): IPictureInPictureState {
	if (value.fromHeader) {
		return {
			...state,
			videoID: value.videoID ? value.videoID : state.videoID,
			videoType: value.videoType ? value.videoType : state.videoType,
			isPiPModeEnabled: !state.isHeaderButtonPressed,
			isHeaderButtonPressed: !state.isHeaderButtonPressed,
		};
	} else if (
		(state.videoID && value.videoID && value.videoID !== state.videoID) ||
		(state.videoType && value.videoType && value.videoType !== state.videoType)
	) {
		return {
			...state,
			videoID: value.videoID ? value.videoID : "",
			videoType: value.videoType ? value.videoType : "",
			isPiPModeEnabled: true,
			isHeaderButtonPressed: false,
		};
	}
	return {
		...state,
		videoID: value.videoID ? value.videoID : "",
		videoType: value.videoType ? value.videoType : "",
		isPiPModeEnabled: !state.isPiPModeEnabled,
		isHeaderButtonPressed: false,
	};
}

function enterPiPMode(
	state: IPictureInPictureState,
	value: { newVideoID: string; newVideoType: VideoType },
): IPictureInPictureState {
	return { ...state, videoID: value.newVideoID, videoType: value.newVideoType, isPiPModeEnabled: true };
}

function leavePiPMode(
	state: IPictureInPictureState,
	value: { videoID?: string; videoType?: string; isMainParticipant: boolean },
): IPictureInPictureState {
	return {
		...state,
		isPiPModeEnabled:
			value.videoID === state.videoID && value.videoType === state.videoType
				? false
				: state.isPiPModeEnabled,
		isHeaderButtonPressed:
			value.isMainParticipant ||
			(value.videoID === state.videoID && value.videoType === state.videoType) ||
			!state.isPiPModeEnabled
				? false
				: state.isHeaderButtonPressed,
	};
}

/// SELECTORS ///

function getPipStatus(state: IPictureInPictureState): boolean {
	return state.isPiPModeEnabled;
}

function getPipVideoID(state: IPictureInPictureState): string {
	return state.videoID;
}

function getPipVideoType(state: IPictureInPictureState): string {
	return state.videoType;
}

function getHeaderButtonPressed(state: IPictureInPictureState): boolean {
	return state.isHeaderButtonPressed;
}

/// BUILD IT ///

const builtState = buildSharedState({
	init: getInitialState,
	reducers: {
		togglePiPMode,
		enterPiPMode,
		leavePiPMode,
	},
	selectors: {
		getPipStatus,
		getPipVideoID,
		getPipVideoType,
		getHeaderButtonPressed,
	},
});

store.addSharedState(builtState.sharedState, "pictureInPicture");

export const {
	actionCreators: pipActions,
	useSharedState: usePictureInPictureState,
	sharedState: state,
} = builtState;
